
import { PrebuiltPC } from "@/types/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/hooks/use-cart";
import { Info } from "lucide-react";
import { useState } from "react";
import DiscountForm from "@/components/Checkout/DiscountForm";
import { supabase } from "@/integrations/supabase/client";

interface OrderSummaryProps {
  product: PrebuiltPC;
  paymentMethod: "stripe" | "paypal" | "zelle";
}

const OrderSummary = ({ product, paymentMethod }: OrderSummaryProps) => {
  const { items } = useCart();
  const [discountAmount, setDiscountAmount] = useState(0);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  
  // Determine if we have marketplace items, custom PCs, or both
  const hasCustomPC = items.some(item => item.product_type === 'custom');
  const hasOnlyMarketplace = items.every(item => item.product_type === 'marketplace');
  
  // Get subtotal of only custom PC items for processing fee calculation
  const getCustomPCSubtotal = (): number => {
    return items
      .filter(item => item.product_type === 'custom')
      .reduce((total, item) => total + item.price * item.quantity, 0);
  };
  
  // Free shipping for all orders
  const calculateShipping = (): number => {
    return 0;
  };
  
  const baseAmount = product.price;
  const assemblyFee = 0; // For Option B, if product is a custom PC, its price (baseAmount) includes the assembly fee. If prebuilt, price is also typically all-inclusive here.
  const shippingFee = 0; // Free shipping
  const tax = Math.round((baseAmount + assemblyFee) * 0.08);
  
  // Only apply credit card fee to custom PC items
  const creditCardFee = (paymentMethod === "stripe" || paymentMethod === "paypal") && hasCustomPC ? 
    Math.round(getCustomPCSubtotal() * 0.03) : 0;
  
  // Final total includes base amount, assembly fee, shipping, tax, credit card fee, and discount
  const total = baseAmount + assemblyFee + shippingFee + tax + creditCardFee - discountAmount;

  const handleApplyAffiliate = (code: string) => {
    setAffiliateCode(code);
    // For now, we'll assume affiliates give a flat 5% discount
    const discount = Math.round(baseAmount * 0.05);
    setDiscountAmount(discount);
  };

  const handleApplyCoupon = async (code: string) => {
    setCouponCode(code);
    
    // Fetch coupon details to get discount amount
    try {
      const { data, error } = await supabase
        .from("coupons")
        .select("discount_type, discount_value")
        .eq("code", code)
        .single();
        
      if (error) throw error;
      
      let discount = 0;
      if (data.discount_type === 'percentage') {
        discount = Math.round(baseAmount * (data.discount_value / 100));
      } else if (data.discount_type === 'fixed') {
        discount = data.discount_value;
      }
      
      setDiscountAmount(discount);
    } catch (error) {
      console.error("Error fetching coupon details:", error);
      // Default to 10% if we can't fetch details
      const discount = Math.round(baseAmount * 0.10);
      setDiscountAmount(discount);
    }
  };

  return (
    <Card className="bg-gaming-dark border-gaming-light-gray/20">
      <CardHeader>
        <CardTitle className="text-gaming-blue">Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Base Price</span>
            <span className="text-white">${baseAmount.toFixed(2)}</span>
          </div>
          
          {/* Only show assembly fee for custom PCs */}
          {hasCustomPC && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Assembly Fee</span>
              <span className="text-white">${assemblyFee.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Shipping</span>
            <span className="text-white text-green-500">FREE</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Tax (8%)</span>
            <span className="text-white">${tax.toFixed(2)}</span>
          </div>

          {/* Only show credit card fee for custom PCs */}
          {creditCardFee > 0 && hasCustomPC && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Processing Fee (3%)</span>
              <span className="text-white">${creditCardFee.toFixed(2)}</span>
            </div>
          )}

          {/* Show discount if applied */}
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-green-500">Discount</span>
              <span className="text-green-500">-${discountAmount.toFixed(2)}</span>
            </div>
          )}

          <Separator className="my-2 bg-gaming-light-gray/20" />
          
          <div className="flex justify-between font-bold">
            <span className="text-white">Total</span>
            <span className="text-gaming-blue">${total.toFixed(2)}</span>
          </div>

          {/* Discount code input form */}
          <DiscountForm
            onApplyAffiliate={handleApplyAffiliate}
            onApplyCoupon={handleApplyCoupon}
            className="mt-4 pt-3 border-t border-gaming-light-gray/20"
          />

          {/* Zelle discount notice */}
          {(paymentMethod === "stripe" || paymentMethod === "paypal") && hasCustomPC && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-sm flex items-start gap-2 mt-3">
              <Info className="text-green-400 mt-0.5 flex-shrink-0" size={16} />
              <p className="text-green-400">
                Save 3% by paying with Zelle! Switch to Zelle to remove the processing fee.
              </p>
            </div>
          )}

          {paymentMethod === "zelle" && hasCustomPC && (
            <p className="text-sm text-green-500 mt-2">
              You're saving 3% by paying with Zelle!
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
